import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import StrategyEditorModal from '../strategy/StrategyEditorModal';
import strategyService from '../../services/strategyService';
import type { StrategyWithLegs } from '../../types';
import { Modal } from '../ui/Modal';

interface StrategyEditLauncherProps {
  strategyId: string;
  basketId: string;
  onClose: () => void;
  /** Fires after a successful edit so the page can refetch schedule data. */
  onUpdated: () => void;
}

/**
 * Opened by the gear icon on the Schedule page. Fetches the full strategy
 * record, then mounts the standard StrategyWizardDialog in edit mode so the
 * user gets the same configuration UX as Basket Management.
 */
const StrategyEditLauncher: React.FC<StrategyEditLauncherProps> = ({
  strategyId,
  basketId,
  onClose,
  onUpdated,
}) => {
  const [strategy, setStrategy] = useState<StrategyWithLegs | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await strategyService.getStrategy(strategyId);
        if (!cancelled) setStrategy(data);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load strategy');
      }
    })();
    return () => { cancelled = true; };
  }, [strategyId]);

  // Loading / error gate — tiny overlay so the user isn't left staring at nothing.
  if (!strategy) {
    return (
      <Modal isOpen onClose={onClose} maxWidth="sm" className="w-auto max-w-none">
        <div className="px-5 py-4 flex items-center gap-3">
          {error ? (
            <>
              <span className="text-sm text-red-600 dark:text-red-400">{error}</span>
              <button
                type="button"
                onClick={onClose}
                className="p-1 rounded-md text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </>
          ) : (
            <span className="text-sm text-gray-500 dark:text-gray-400">Loading strategy…</span>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <StrategyEditorModal
      basketId={basketId}
      editingStrategy={strategy}
      onCancel={onClose}
      onDone={(result: any) => {
        // The panel's submission hook already called the update API.
        // We just need to close + let the page refresh its data.
        if (result?.success !== false) onUpdated();
        onClose();
      }}
    />
  );
};

export default StrategyEditLauncher;